// src/components/Hint.tsx (New Reusable Component)
import React, { ReactNode, useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import './components.css';

// This hook can also live in a dedicated utils/hooks.ts file if preferred
export function useResetHintsFromQuery(prefix: string) {
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset-hints") === "1") {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i) || "";
        if (k.startsWith(prefix)) keysToRemove.push(k);
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
      setRefresh((r) => r + 1);
    }
  }, [prefix]);
  return refresh;
}

interface HintProps {
  id: string;
  category: 'tag' | 'preview' | 'live';
  children: ReactNode;
  style?: React.CSSProperties;
  className?: string;
  compact?: boolean;
  refreshToken?: number;
}

export default function Hint({
  id,
  category,
  children,
  style,
  className,
  compact = false,
  refreshToken = 0,
}: HintProps) {
  const storageKey = `cueboard.hint.${category}.${id}`;
  const computeVisible = () => {
    try {
      return localStorage.getItem(storageKey) !== "dismissed";
    } catch {
      return true;
    }
  };
  const [visible, setVisible] = useState<boolean>(computeVisible);
  useEffect(() => { setVisible(computeVisible()); }, [refreshToken, storageKey]);
  
  if (!visible) return null;

  const baseClasses = `hint ${compact ? 'compact' : ''}`;

  return (
    <div className={`${baseClasses} ${className ?? ''}`} style={style} role="note" aria-live="polite">
      <Info size={16} className="hint-icon" aria-hidden />
      <div className="hint-content">{children}</div>
      <button
        type="button"
        aria-label="Dismiss hint"
        onClick={() => {
          try { localStorage.setItem(storageKey, "dismissed"); } catch {}
          setVisible(false);
        }}
        className="button button-ghost hint-dismiss"
      >
        ×
      </button>
    </div>
  );
}